import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getUgcShare, UgcShare } from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { formatTime } from '../../utils/time';
import { AuditBadge } from './ugcLabels';
import { BeforeAfterImages } from './parts';
import { RestoredBadge } from './processHistoryLabels';
import { useMModeText } from '../../utils/mmodeText';
import { useDictText, useProcessTypeText } from '../../utils/dictOptions';
import { thumbUrl, THUMB_W_SM } from '../../utils/imgUrl';

// UGC 分享详情页 (/assets/ugc-shares/:id): 与其他模块一致走独立路由页 (不是弹窗).
// 左列图片(封面/效果图/行为回流), 右列基础信息 + 参数快照全量 + 质检 + 小贴士快照.
// 2026-09-23 改版: 左栏封面由单张效果图改成「加工前 / 加工后」对比, 复用 parts.BeforeAfterImages
// (与历史加工数据详情同款) —— 加工前那张是视觉模型从效果图反推出来的还原图, 不是实拍, 挂角标说清楚.

function UgcShareDetail() {
    // 加工模式 / 工艺类型 / 光源均从平台字典取名 (mm_mode + dict process_type/light_source).
    const modeText = useMModeText();
    const typeText = useProcessTypeText();
    const laserText = useDictText('light_source');
    const { id } = useParams<{ id: string }>();
    const [d, setD] = useState<UgcShare | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        getUgcShare(Number(id))
            .then((r) => { if (alive) setD(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)); });
        return () => { alive = false; };
    }, [id]);

    const snapshot = useMemo(() => {
        if (!d) return [] as [string, string][];
        try {
            const obj = JSON.parse(d.paraSnapshot || '{}') as Record<string, unknown>;
            return Object.entries(obj).map(
                ([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)] as [string, string],
            );
        } catch { return []; }
    }, [d]);
    const effectImages = useMemo(() => {
        if (!d) return [] as string[];
        try { return (JSON.parse(d.effectImages || '[]') as string[]).filter(Boolean); } catch { return []; }
    }, [d]);
    const actions = useMemo(() => {
        if (!d) return [] as [string, number][];
        try {
            return Object.entries(JSON.parse(d.actionStats || '{}') as Record<string, number>)
                .sort((a, b) => b[1] - a[1]);
        } catch { return []; }
    }, [d]);
    const actionCount = (k: string) => actions.find(([a]) => a === k)?.[1] ?? 0;
    const tips = useMemo(() => {
        if (!d) return [] as { code?: string; title?: string; note?: string }[];
        try {
            return JSON.parse(d.processingTips || '[]') as { code?: string; title?: string; note?: string }[];
        } catch { return []; }
    }, [d]);

    if (error) {
        return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    }
    if (!d) {
        return <div className="text-center text-slate-400 text-sm py-16">加载中…</div>;
    }

    const info: [string, React.ReactNode][] = [
        ['源参数ID', String(d.sourceId)],
        ['作者', `${d.creatorName || '—'} (uid ${d.creatorId})`],
        ['机型', `${d.deviceName || d.deviceCode}${d.deviceKey ? ` · ${d.deviceKey}` : ''}`],
        ['光源/功率', `${laserText(d.laserSourceCode)} ${d.laserPowerCode ? `${d.laserPowerCode}W` : ''}`],
        ['分类', `${d.categoryCode || '—'} / ${d.categoryCodeL2 || '—'}`],
        ['AI 分类', d.aiCategoryL1 ? `${d.aiCategoryL1} / ${d.aiCategoryL2}` : '—'],
        ['加工模式', `${modeText(d.processingModeCode)} · ${typeText(d.processingTypeCode)}`],
        ['厚度/颜色', `${d.thickness != null ? `${d.thickness}mm` : '—'} · ${d.color || '—'}`],
        ['品牌', d.brand || '—'],
        ['是否通过', <AuditBadge key="a" status={d.auditStatus} />],
        ['内容状态', d.ugcStatus],
        ['AI 质检', d.qualityLabel ? `${d.qualityLabel} (${d.evaluationSource})` : '—'],
        ['投稿渠道', d.sourceType || '—'],
        ['发布时间', formatTime(d.publishedAt)],
        ['同步时间', formatTime(d.sourceSyncedAt)],
    ];

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs text-slate-400 mb-1">
                        <Link to="/assets/ugc-shares" className="text-brand-600 hover:underline">
                            ← 返回 UGC 分享
                        </Link>
                    </div>
                    <div className="text-base font-semibold truncate" title={d.materialName}>
                        {d.materialName || d.sourceId}
                    </div>
                    {d.description && <div className="text-xs text-slate-500 mt-1">{d.description}</div>}
                </div>
                <AuditBadge status={d.auditStatus} />
            </div>

            <div className="grid grid-cols-[320px_1fr] gap-5 items-start">
                {/* 左: 图片 + 行为回流 */}
                <div className="space-y-3">
                    <div className="card p-3 space-y-3">
{/* 没有还原图时只摆一张封面 —— 硬塞一个"加工前:无"的占位, 对还没还原的那 6.6% 只是噪音 */}
                        {d.restoredImageUrl ? (
                            <BeforeAfterImages
                                before={d.restoredImageUrl}
                                after={d.coverImage}
                                size={280}
                                onZoom={setZoomSrc}
                                beforeBadge={<RestoredBadge compact />}
                                afterLabel="加工后 (封面图)"
                                afterMissingText="无 —— 这条分享没有效果图"
                            />
                        ) : d.coverImage ? (
                            <img
                                src={thumbUrl(d.coverImage, 640)}
                                alt={d.materialName}
                                loading="lazy"
                                decoding="async"
                                className="w-full rounded-lg object-cover cursor-zoom-in border border-slate-100"
                                style={{ maxHeight: 280 }}
                                onClick={() => setZoomSrc(d.coverImage)}
                            />
                        ) : (
                            <div className="w-full h-40 rounded-lg bg-slate-100" />
                        )}
                        {effectImages.length > 1 && (
                            <div className="flex flex-wrap gap-2">
                                {effectImages.map((u) => (
                                    <img key={u} src={thumbUrl(u, THUMB_W_SM)} alt="effect" loading="lazy" decoding="async"
                                        className="w-14 h-14 rounded-lg object-cover cursor-zoom-in border border-slate-100"
                                        onClick={() => setZoomSrc(u)} />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="card p-3">
                        <div className="text-xs font-semibold text-slate-500 mb-2">行为回流</div>
                        {/* 加工次数 = 存进 Studio + 存进 XCS (社区侧口径, 后端现算), 0 也显示, 免得被当成"没统计" */}
                        <div className="text-xs text-slate-600 mb-1.5">
                            加工次数: <b className={d.processCount > 0 ? 'text-sky-600' : ''}>{d.processCount}</b>
                            <span className="ml-1 text-slate-400">
                                (Studio {actionCount('save_in_studio')} · XCS {actionCount('save_in_xcs')})
                            </span>
                        </div>
                        {actions.length > 0 && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                                {actions.map(([k, v]) => (
                                    <span key={k}>
                                        {k === 'worked' ? '👍 有用' : k === 'unworked' ? '👎 没用' : k}: <b>{v}</b>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 右: 信息 + 参数快照 */}
                <div className="space-y-4">
                    <div className="card p-4">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[13px]">
                            {info.map(([k, v]) => (
                                <div key={k as string} className="flex gap-2">
                                    <span className="text-slate-400 shrink-0 w-[72px]">{k}</span>
                                    <span className="text-slate-700 min-w-0 break-all">{v}</span>
                                </div>
                            ))}
                        </div>
                        {d.evaluationReason && (
                            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2.5 mt-3">
                                <b>质检理由:</b> {d.evaluationReason}
                            </div>
                        )}
                    </div>
                    <div className="card p-4">
                        <div className="text-xs font-semibold text-slate-500 mb-2">参数快照 (全量)</div>
                        {snapshot.length === 0 ? (
                            <div className="text-xs text-slate-400">无</div>
                        ) : (
                            <div className="grid grid-cols-4 gap-1.5">
                                {snapshot.map(([k, v]) => (
                                    <div key={k} className="rounded-lg bg-slate-50 px-2 py-1.5">
                                        <div className="text-[10px] text-slate-400 font-mono truncate" title={k}>{k}</div>
                                        <div className="text-xs text-slate-700 font-medium break-all">{v}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {tips.length > 0 && (
                        <div className="card p-4">
                            <div className="text-xs font-semibold text-slate-500 mb-2">加工小贴士 (源快照)</div>
                            <ul className="space-y-1 text-xs text-slate-600 list-disc pl-4">
                                {tips.map((t, i) => (
                                    <li key={t.code || i}>
                                        <b>{t.title}</b>
                                        {t.note ? ` — ${t.note}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default UgcShareDetail;
